/**
 * Authentication utilities for OIDC-based auth.
 *
 * Provides types and functions for user authentication state management.
 */

export interface User {
  sub: string;
  email: string | null;
  name: string | null;
  isAdmin: boolean;
}

/**
 * Fetch the current authenticated user from the backend.
 * Returns null if not authenticated (401 response).
 */
export async function fetchCurrentUser(): Promise<User | null> {
  const res = await fetch("/api/auth/me", { credentials: "include" });
  if (!res.ok) return null;
  const data = await res.json();
  return {
    sub: data.sub,
    email: data.email,
    name: data.name,
    isAdmin: data.is_admin,
  };
}

/**
 * Logout the current user by posting to the logout endpoint.
 * Uses form submission to ensure proper cookie handling.
 */
export function logout(): void {
  const form = document.createElement("form");
  form.method = "POST";
  form.action = "/api/auth/logout";
  document.body.appendChild(form);
  form.submit();
}

/**
 * Redirect to the login page with an optional return URL.
 * Defaults to the current pathname if no returnTo is provided.
 */
export function redirectToLogin(returnTo?: string): void {
  const path = returnTo || globalThis.location.pathname;
  globalThis.location.href = `/api/auth/login?return_to=${encodeURIComponent(path)}`;
}
